import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Snackbar,
  InputAdornment,
  Alert,
  CircularProgress,
  Paper
} from "@mui/material";
import { Email, ArrowBack, Send } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [emailSent, setEmailSent] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setSnackbar({
        open: true,
        message: "Veuillez entrer votre adresse email",
        severity: "error"
      });
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setEmailSent(true);
      setSnackbar({
        open: true,
        message: "Email de réinitialisation envoyé ! Vérifiez votre boîte de réception.",
        severity: "success"
      });
    } catch (error) {
      let errorMessage = "Une erreur est survenue. Veuillez réessayer.";
      
      // Handle specific Firebase errors
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = "Aucun compte trouvé avec cette adresse email.";
          break;
        case 'auth/invalid-email':
          errorMessage = "Adresse email invalide.";
          break;
        case 'auth/too-many-requests':
          errorMessage = "Trop de tentatives. Veuillez réessayer plus tard.";
          break;
        default:
          errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleBackToLogin = () => {
    navigate("/");
  };

  return (
    <Grid
      container
      sx={{
        minHeight: "100vh",
        background: "#f3f4f6"
      }}
    >
      {/* LEFT SIDE - Decorative Section */}
      <Grid
        item
        md={6}
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          pl: 10,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            variant="h5"
            sx={{ 
              fontWeight: 700, 
              mb: 3,
              color: "white",
              opacity: 0.9
            }}
          >
            Restaurant ERP
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              mb: 3,
              lineHeight: 1.2,
              color: "white"
            }}
          >
            Mot de passe oublié ?
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.9)",
              mb: 4,
              maxWidth: 450,
              fontSize: "1.1rem"
            }}
          >
            Ne vous inquiétez pas ! Entrez votre email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Typography sx={{ color: "white", fontWeight: 600, mb: 1 }}>
              💡 Astuce
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
              Vérifiez vos spams si vous ne recevez pas l'email dans quelques minutes.
            </Typography>
          </Box>
        </Box>

        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
            zIndex: 1
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
            zIndex: 1
          }}
        />
      </Grid>

      {/* RIGHT SIDE - Forgot Password Form */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 450,
            background: "white",
            borderRadius: "24px",
            p: { xs: 3, sm: 4 },
            boxShadow: "0 20px 35px rgba(0,0,0,0.1)"
          }}
        >
          {!emailSent ? (
            <>
              <Box sx={{ mb: 4, textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, mb: 1, color: "#1f2937" }}
                >
                  Mot de passe oublié ?
                </Typography>
                <Typography
                  sx={{ color: "#6b7280", mt: 1 }}
                >
                  Entrez votre email pour réinitialiser votre mot de passe
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Adresse Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: "12px" }
                  }}
                  helperText="Entrez l'email associé à votre compte"
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    background: "linear-gradient(90deg, #2563eb, #9333ea)",
                    py: 1.5,
                    borderRadius: "12px",
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "1rem",
                    mb: 2,
                    '&:hover': {
                      background: "linear-gradient(90deg, #1d4ed8, #7e22ce)",
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <>
                      <Send sx={{ mr: 1, fontSize: 20 }} />
                      Envoyer le lien de réinitialisation
                    </>
                  )}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={handleBackToLogin}
                  startIcon={<ArrowBack />}
                  sx={{
                    color: "#6b7280",
                    textTransform: "none",
                    '&:hover': {
                      backgroundColor: "transparent",
                      color: "#2563eb"
                    }
                  }}
                >
                  Retour à la connexion
                </Button>
              </Box>
            </>
          ) : (
            // Success State
            <Box sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3
                }}
              >
                <Send sx={{ fontSize: 40, color: "white" }} />
              </Box>

              <Typography
                variant="h5"
                sx={{ fontWeight: 700, mb: 2, color: "#1f2937" }}
              >
                Email envoyé !
              </Typography>

              <Typography
                sx={{ color: "#6b7280", mb: 3, lineHeight: 1.6 }}
              >
                Nous avons envoyé un lien de réinitialisation du mot de passe à :
                <br />
                <strong style={{ color: "#2563eb" }}>{email}</strong>
              </Typography>

              <Alert 
                severity="info" 
                sx={{ mb: 3, borderRadius: "12px", textAlign: "left" }}
              >
                <Typography variant="body2">
                  📧 Vérifiez votre boîte de réception et suivez les instructions.
                  <br />
                  💡 Pensez à vérifier vos spams si vous ne trouvez pas l'email.
                </Typography>
              </Alert>

              <Button
                fullWidth
                variant="contained"
                onClick={handleBackToLogin}
                sx={{
                  background: "linear-gradient(90deg, #2563eb, #9333ea)",
                  py: 1.5,
                  borderRadius: "12px",
                  fontWeight: 600,
                  textTransform: "none",
                  mb: 2
                }}
              >
                Retour à la connexion
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => setEmailSent(false)}
                sx={{
                  color: "#6b7280",
                  textTransform: "none"
                }}
              >
                Modifier l'adresse email
              </Button>
            </Box>
          )}
        </Paper>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default ForgotPassword;